/**
 * Dashboard and Other APIs
 * Handles dashboard, notifications, and analyze API calls
 */

import apiClient from './index.js';
import { REPORTING_DASHBOARD_ENABLED } from '../../config.js';
import notificationWebSocketService from '../websocket/notificationWebSocket.js';

export class DashboardApi {
  /**
   * Get dashboard data.
   *
   * SS-242: Calls the reporting dashboard endpoint first (/reporting/dashboard),
   * with a safe fallback to the legacy dashboard endpoint (/dashboard/v1/list)
   * when reporting is unavailable (5xx, 404, 408, 429, network error).
   *
   * Auth failures (401/403) are propagated without fallback — both endpoints
   * share the same token authorizer, so a 401 from reporting means the token
   * is invalid and legacy would also reject it.
   *
   * Feature flag VITE_REPORTING_DASHBOARD_ENABLED (default: enabled) controls
   * whether reporting is primary. When disabled, goes straight to legacy.
   *
   * @param {string} token - Auth token
   * @returns {Promise<ApiResponse>} Dashboard response with legacy list fields
   */
  static async getDashboard(token) {
    // Feature flag disabled — legacy only
    if (!REPORTING_DASHBOARD_ENABLED) {
      return apiClient.get('/dashboard/v1/list', token);
    }

    // Reporting primary — fail fast (retries: 0) so fallback is immediate
    const reportingResponse = await apiClient.get('/reporting/dashboard', token, { retries: 0 });

    if (reportingResponse.success) {
      return reportingResponse;
    }

    // Do not mask auth failures — propagate 401/403 to the caller
    if (reportingResponse.status === 401 || reportingResponse.status === 403) {
      return reportingResponse;
    }

    // Fallback for any non-auth reporting failure (5xx/404/408/429/network)
    const legacyResponse = await apiClient.get('/dashboard/v1/list', token);
    return legacyResponse.success ? legacyResponse : reportingResponse;
  }
}

export class NotificationsApi {
  /**
   * Get list of notifications (HTTP fallback)
   */
  static async getNotifications(token) {
    return apiClient.get('/notifications/v1/list', token);
  }

  /**
   * Update notifications
   * @param {string} token - Auth token
   * @param {Object} updateData - Update data
   */
  static async updateNotifications(token, updateData) {
    return apiClient.post('/notifications/v1/update', token, updateData);
  }

  /**
   * Connect to real-time notifications via WebSocket
   * @param {string} token - Auth token
   * @param {Function} onNotification - Callback for new notifications
   * @param {Function} onConnection - Callback for connection status changes
   */
  static connectRealTime(token, onNotification, onConnection) {
    const removeListener = notificationWebSocketService.addListener((data) => {
      if (data.type === 'notification' && onNotification) {
        onNotification(data.data);
      } else if (data.type === 'connection' && onConnection) {
        onConnection(data);
      }
    });

    notificationWebSocketService.connect(token);

    return {
      disconnect: () => {
        removeListener();
        notificationWebSocketService.disconnect();
      },
      isConnected: () => notificationWebSocketService.isConnected(),
      getStatus: () => notificationWebSocketService.getStatus()
    };
  }

  /**
   * Disconnect from real-time notifications
   */
  static disconnectRealTime() {
    notificationWebSocketService.disconnect();
  }
}

export class AnalyzeApi {
  /**
   * Add analysis
   * @param {string} token - Auth token
   * @param {Object} analysisData - Analysis data
   */
  static async addAnalysis(token, analysisData) {
    return apiClient.post('/media/v1/analyze', token, analysisData);
  }
}

export { DashboardApi as default };
